// https://gist.github.com/mikelehen/3596a30bd69384624c11
/**
 * Fancy ID generator that creates 20-character string identifiers with the following properties:
 *
 * 1. They're based on timestamp so that they sort *after* any existing ids.
 * 2. They contain 72-bits of random data after the timestamp so that IDs won't collide with other clients' IDs.
 * 3. They sort *lexicographically* (so the timestamp is converted to characters that will sort properly).
 * 4. They're monotonically increasing.  Even if you generate more than one in the same timestamp, the
 *    latter ones will sort after the former ones.  We do this by using the previous random bits
 *    but "incrementing" them by 1 (only in the case of a timestamp collision).
 */

export class FirebasePushID {
  // Timestamp of last push, used to prevent local collisions if you push twice in one ms.
  private static lastPushTime = 0;

  // Modeled after base64 web-safe chars, but ordered by ASCII.
  private static PUSH_CHARS =
    "-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz";

  // We generate 72-bits of randomness which get turned into 12 characters and appended to the
  // timestamp to prevent collisions with other clients.  We store the last characters we
  // generated because in the event of a collision, we'll use those same characters except
  // "incremented" by one.
  private static lastRandChars: number[] = new Array(12).fill(0);

  // Prefer these static properties over the methods commented out below as we'd like for these to be true constants as opposed to giving a new value every time
  public static FirstID = "-".repeat(20); // this.PUSH_CHARS[0].repeat(20)
  public static LastID = "z".repeat(20); // his.PUSH_CHARS.slice(-1)[0].repeat(20)

  private static getIDFromTimestamp(now: number) {
    const timeStampChars = new Array(8);
    let i: number;
    for (i = 7; i >= 0; i--) {
      // 8 iterations at a bit width of 2^6 = 2^48-1 timestamps that we can represent
      timeStampChars[i] = this.PUSH_CHARS.charAt(now % 64);
      // NOTE: Can't use << here because javascript will convert to int and lose the upper bits.
      now = Math.floor(now / 64);
    }
    if (now !== 0)
      throw new Error("We should have converted the entire timestamp.");

    const id = timeStampChars.join("");

    return id;
  }

  // Generates chronologically orderable unique string one by one
  public static generate(timestamp?: number): string {
    const ts = timestamp ?? new Date().getTime();
    const duplicateTime = ts === this.lastPushTime;
    this.lastPushTime = ts;
    const idObj = this.getIDFromTimestamp(ts);

    let i: number;
    let id = idObj;

    if (!duplicateTime) {
      for (i = 0; i < 12; i++) {
        this.lastRandChars[i] = Math.floor(Math.random() * 64);
      }
    } else {
      // If the timestamp hasn't changed since last push, use the same random number, except incremented by 1.
      for (i = 11; i >= 0 && this.lastRandChars[i] === 63; i--) {
        this.lastRandChars[i] = 0;
      }
      this.lastRandChars[i]++;
    }
    for (i = 0; i < 12; i++) {
      id += this.PUSH_CHARS.charAt(this.lastRandChars[i]);
    }
    if (id.length != 20) throw new Error("Length should be 20.");

    return id;
  }

  public static startID(timestamp?: number): string {
    const now = timestamp ?? new Date().getTime();
    let id = this.getIDFromTimestamp(now);
    id += this.PUSH_CHARS[0].repeat(12);

    return id;
  }

  public static endID(timestamp?: number): string {
    const now = timestamp ?? new Date().getTime();
    let id = this.getIDFromTimestamp(now);
    id += this.PUSH_CHARS.slice(-1)[0].repeat(12);

    return id;
  }

  // Get the last ID, one millisecond before this one
  public static previousMillisecondID(pushID: string): string {
    if (!this.isValid(pushID)) {
      throw new Error(`Firebase Push ID ${pushID} is Invalid!`);
    }

    // There could be more error checking on the timestamp range, but if we are at the beginning or end of millisecond time and using this incorrectly, we have bigger problems

    const ts = this.getTimestamp(pushID);
    return this.endID(ts - 1);
  }

  // Get the first ID, one millisecond after this one
  public static nextMillisecondID(pushID: string): string {
    if (!this.isValid(pushID)) {
      throw new Error(`Firebase Push ID ${pushID} is Invalid!`);
    }

    // There could be more error checking on the timestamp range, but if we are at the beginning or end of millisecond time and using this incorrectly, we have bigger problems

    const ts = this.getTimestamp(pushID);
    return this.startID(ts + 1);
  }

  public static previousID(pushID: string): string {
    if (!this.isValid(pushID)) {
      throw new Error(`Firebase Push ID ${pushID} is Invalid!`);
    }

    if (pushID === this.FirstID) {
      throw new Error(
        "ID is the beginning of time! There are no previous push IDs!",
      );
    }

    // Sigh... A string is a primitive value, it's immutable.
    const pushIdChars = [...pushID];

    let index = pushIdChars.length - 1;
    // If we have a '-', we need to turn it to a 'z' and roll back the previous character
    while (pushIdChars[index] === this.PUSH_CHARS[0]) {
      pushIdChars[index] = this.PUSH_CHARS[this.PUSH_CHARS.length - 1];
      index--;
    }

    pushIdChars[index] =
      this.PUSH_CHARS[this.PUSH_CHARS.indexOf(pushIdChars[index]) - 1];

    return pushIdChars.join("");
  }

  public static nextID(pushID: string): string {
    if (!this.isValid(pushID)) {
      throw new Error(`Firebase Push ID ${pushID} is Invalid!`);
    }

    if (pushID === this.LastID) {
      throw new Error("ID is the end of time! There are no next push IDs!");
    }

    // Sigh... A string is a primitive value, it's immutable.
    const pushIdChars = [...pushID];

    let index = pushIdChars.length - 1;
    // If we have a 'z', we need to turn it to a '-' and roll back the previous character
    while (pushIdChars[index] === this.PUSH_CHARS[this.PUSH_CHARS.length - 1]) {
      pushIdChars[index] = this.PUSH_CHARS[0];
      index--;
    }

    pushIdChars[index] =
      this.PUSH_CHARS[this.PUSH_CHARS.indexOf(pushIdChars[index]) + 1];

    return pushIdChars.join("");
  }

  public static isValid(pushID: string): boolean {
    const matchArray: RegExpMatchArray | null = pushID.match(
      /^[-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz]{20}$/,
    );

    if (!matchArray) return false;

    return matchArray.length > 0;
  }

  /**
   * Timestamp
   * Gets timestamp from provided uid.
   */
  public static getTimestamp(pushID: string): number {
    if (!this.isValid(pushID)) {
      throw new Error(`Firebase Push ID ${pushID} is Invalid!`);
    }

    let time = 0;
    const data = pushID.substr(0, 8);

    for (let i = 0; i < 8; i++) {
      time = time * 64 + this.PUSH_CHARS.indexOf(data[i]);
    }

    return time;
  }
}
